import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, Send, Mail, Reply, Settings, Inbox, Clock,
  MessageSquare, ChevronDown, ChevronRight, ExternalLink, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const SENT_CATEGORIES = [
  { id: "booking",      label: "Bookings",      icon: Mail },
  { id: "reschedule",   label: "Rescheduled",   icon: Clock },
  { id: "cancellation", label: "Cancellations",  icon: MessageSquare },
  { id: "support",      label: "Support",        icon: Mail },
];

const FROM_EMAILS = {
  booking:      "hello@info.niskyauto.live",
  reschedule:   "reschedule@info.niskyauto.live",
  cancellation: "goodbye@info.niskyauto.live",
};

export default function AdminInbox() {
  const { user } = useAuth();
  const [tab, setTab] = useState("sent");
  const [sentCategory, setSentCategory] = useState("booking");
  const [sentEmails, setSentEmails] = useState([]);
  const [receivedEmails, setReceivedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [threads, setThreads] = useState({});
  const [loadingThread, setLoadingThread] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [sendingReply, setSendingReply] = useState(false);

  const [directTo, setDirectTo] = useState("");
  const [directSubject, setDirectSubject] = useState("");
  const [directBody, setDirectBody] = useState("");
  const [sendingDirect, setSendingDirect] = useState(false);

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);

  const isOwner = user?.email === "jerometsamuel@gmail.com";

  useEffect(() => {
    load();
    loadConfig();
  }, []);

  useEffect(() => {
    if (tab === "sent") loadSent();
    if (tab === "received") loadReceived();
  }, [tab, sentCategory]);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/admin/inbox/config");
      setConfig(data);
    } catch { /* ignore */ }
    setConfigLoading(false);
  };

  const switchProvider = async (provider) => {
    try {
      await api.put("/admin/inbox/config", { active_provider: provider });
      setConfig((c) => ({ ...c, active_provider: provider }));
      toast.success(`Switched to ${provider === "brevo" ? "Brevo" : "Resend"}`);
    } catch (e) {
      toast.error(errMsg(e, "Failed to update config"));
    }
  };

  const load = async () => {
    setLoading(true);
    await Promise.all([loadSent(), loadReceived()]);
    setLoading(false);
  };

  const loadSent = async () => {
    try {
      const { data } = await api.get(`/admin/inbox/sent?category=${sentCategory}`);
      setSentEmails(data.emails || []);
    } catch { setSentEmails([]); }
  };

  const loadReceived = async () => {
    try {
      const { data } = await api.get("/admin/inbox/received");
      setReceivedEmails(data.emails || []);
    } catch { setReceivedEmails([]); }
  };

  const expandEmail = async (email, emailId) => {
    if (expandedId === emailId) { setExpandedId(null); return; }
    setExpandedId(emailId);
    // Fetch thread if not already loaded
    if (!threads[emailId]) {
      setLoadingThread(emailId);
      try {
        const { data } = await api.get(`/admin/inbox/thread/${emailId}`);
        setThreads(prev => ({ ...prev, [emailId]: data.thread || [email] }));
      } catch { setThreads(prev => ({ ...prev, [emailId]: [email] })); }
      setLoadingThread(null);
    }
    // Mark as read
    try { await api.post(`/admin/inbox/mark-read/${emailId}`); } catch {}
  };

  const handleReply = async (emailId) => {
    const text = replyTexts[emailId] || "";
    if (!text.trim()) return toast.error("Enter a reply");
    setSendingReply(true);
    try {
      await api.post(`/admin/inbox/reply/${emailId}`, { message: text });
      toast.success("Reply sent");
      setReplyTexts(prev => ({ ...prev, [emailId]: "" }));
      // Re-fetch thread to show the new reply
      const { data } = await api.get(`/admin/inbox/thread/${emailId}`);
      setThreads(prev => ({ ...prev, [emailId]: data.thread || [] }));
    } catch (e) {
      toast.error(errMsg(e, "Failed to send reply"));
    } finally {
      setSendingReply(false);
    }
  };

  const handleClear = async (category) => {
    const label = category === "received" ? "received" : `${category} sent`;
    if (!window.confirm(`Permanently delete all ${label} emails? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/admin/inbox/clear/${category}`);
      toast.success(`Deleted ${data.deleted || 0} emails`);
      if (tab === "sent") loadSent();
      else loadReceived();
    } catch (e) {
      toast.error(errMsg(e, "Failed to clear"));
    }
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm("Permanently delete this email?")) return;
    try {
      await api.delete(`/admin/inbox/email/${emailId}`);
      toast.success("Email deleted");
      setExpandedId(null);
      setThreads(prev => { const next = { ...prev }; delete next[emailId]; return next; });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to delete"));
    }
  };

  const handleDirectSend = async (e) => {
    e.preventDefault();
    if (!directTo.trim() || !directSubject.trim() || !directBody.trim()) {
      return toast.error("Fill in all fields");
    }
    setSendingDirect(true);
    try {
      await api.post("/admin/inbox/send-direct", {
        to_email: directTo,
        subject: directSubject,
        body: directBody,
      });
      toast.success("Email sent");
      setDirectTo(""); setDirectSubject(""); setDirectBody("");
      loadSent();
    } catch (e) {
      toast.error(errMsg(e, "Failed to send"));
    } finally {
      setSendingDirect(false);
    }
  };

  function fmtTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }

  const recipientList = (to) => (Array.isArray(to) ? to.join(", ") : to || "");

  return (
    <div data-testid="admin-inbox">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="overline mb-1">Inbox</p>
          <h1 className="font-serif text-3xl">Email</h1>
        </div>
        {isOwner && (
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="btn-secondary"
            style={{ padding: "8px 12px", fontSize: "12px" }}
            title="Email provider settings"
          >
            <Settings size={14} /> {showConfig ? "Close" : "Settings"}
          </button>
        )}
      </div>

      {/* Config panel (owner only) */}
      <AnimatePresence>
        {showConfig && isOwner && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="tile-spec p-5">
              <p className="overline mb-4">Email Provider</p>
              {configLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => switchProvider("brevo")}
                    className={`px-5 py-3 rounded-lg border text-sm transition-all ${
                      config?.active_provider === "brevo"
                        ? "bg-foreground text-background border-cream"
                        : "text-foreground/70 border-stroke hover:border-gold"
                    }`}
                  >
                    <p className="font-semibold">Brevo</p>
                    <p className="text-[11px] text-faint mt-1">{config?.brevo_count_month ?? 0} sent this month / {config?.brevo_count_total ?? 0} total</p>
                  </button>
                  <button
                    onClick={() => switchProvider("resend")}
                    className={`px-5 py-3 rounded-lg border text-sm transition-all ${
                      config?.active_provider === "resend"
                        ? "bg-foreground text-background border-cream"
                        : "text-foreground/70 border-stroke hover:border-gold"
                    }`}
                  >
                    <p className="font-semibold">Resend</p>
                    <p className="text-[11px] text-faint mt-1">{config?.resend_count_month ?? 0} sent this month / {config?.resend_count_total ?? 0} total</p>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-stroke">
        {[
          { id: "sent",     label: "Sent",     icon: Send },
          { id: "received", label: "Received", icon: Inbox },
          { id: "send",     label: "Send Email", icon: Mail },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm transition-all border-b-2 ${
                active ? "text-gold border-gold" : "text-foreground/60 border-transparent hover:text-foreground"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={20} className="animate-spin text-muted" />
        </div>
      ) : (
        <>
          {/* ─── SENT ──────────────────────────────── */}
          {tab === "sent" && (
            <div>
              <div className="flex gap-2 mb-5">
                {SENT_CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const active = sentCategory === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSentCategory(c.id)}
                      className={`flex items-center gap-2 px-4 py-2 text-xs rounded-lg border transition-all ${
                        active
                          ? "bg-foreground text-background border-cream"
                          : "text-foreground/70 border-stroke hover:border-gold"
                      }`}
                    >
                      <Icon size={12} /> {c.label}
                    </button>
                  );
                })}
                {isOwner && (
                  <button
                    onClick={() => handleClear(sentCategory)}
                    className="text-[11px] text-red-400/70 hover:text-red-400 ml-auto px-3 py-2"
                    title={`Clear all ${sentCategory} emails`}
                  >
                    <Trash2 size={12} /> Clear
                  </button>
                )}
              </div>

              {sentEmails.length === 0 ? (
                <p className="text-muted text-sm py-10 text-center">No sent emails in this category.</p>
              ) : (
                <div className="space-y-2">
                  {sentEmails.map((e) => (
                    <div key={e.email_id} className="tile-spec p-4">
                      <button
                        onClick={() => expandEmail(e, e.email_id)}
                        className="w-full flex items-start justify-between gap-3 text-left"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-foreground font-medium truncate">{e.subject}</p>
                          <p className="text-xs text-faint mt-0.5">
                            To: {recipientList(e.to_emails)} · {fmtTime(e.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                            e.status === "sent" ? "text-green-400 bg-green-400/10" : "text-red-400 bg-red-400/10"
                          }`}>{e.status}</span>
                          {expandedId === e.email_id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </div>
                      </button>

                      <AnimatePresence>
                        {expandedId === e.email_id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 pt-4 border-t border-stroke space-y-2 text-sm text-foreground/80">
                              {loadingThread === e.email_id ? (
                                <div className="flex items-center justify-center py-4"><Loader2 size={14} className="animate-spin text-muted" /></div>
                              ) : (
                                <>
                                  {(threads[e.email_id] || [e]).map((m, idx) => (
                                    <div key={m.email_id} className={`p-3 rounded ${idx > 0 ? 'mt-3' : ''}`} style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs text-faint">
                                          <span className="text-foreground font-medium">{m.direction === 'sent' ? 'To' : 'From'}:</span> {m.from_name || m.from_email}
                                          {m.direction === 'sent' ? ` → ${recipientList(m.to_emails)}` : ''}
                                        </div>
                                        <span className="text-[10px] text-faint shrink-0 ml-2">{fmtTime(m.created_at)}</span>
                                      </div>
                                      <div className="text-sm leading-relaxed inbox-body" dangerouslySetInnerHTML={{ __html: m.body_html || '<em class="text-faint">(No content)</em>' }} />
                                    </div>
                                  ))}
                                  <div className="mt-3 pt-3 border-t border-stroke">
                                    <p className="text-xs text-faint mb-2">Reply to: {e.from_name || e.from_email}</p>
                                    <textarea
                                      className="input-luxe w-full"
                                      rows={2}
                                      value={replyTexts[e.email_id] || ''}
                                      onChange={(ev) => { setReplyTexts(prev => ({ ...prev, [e.email_id]: ev.target.value })); }}
                                      placeholder="Type your reply..."
                                    />
                                    <div className="flex gap-2 mt-2 justify-end">
                                      <button className="btn-primary" style={{ padding: "6px 12px", fontSize: "11px" }} onClick={() => handleReply(e.email_id)} disabled={sendingReply}>
                                        {sendingReply ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                                        Send
                                      </button>
                                      {isOwner && (
                                        <button onClick={() => handleDelete(e.email_id)} className="btn-secondary" style={{ padding: "6px 12px", fontSize: "11px", color: "#F44336" }}>
                                          <Trash2 size={11} /> Delete
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── RECEIVED ──────────────────────────── */}
          {tab === "received" && (
            <div>
              {isOwner && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => handleClear("received")}
                    className="text-[11px] text-red-400/70 hover:text-red-400 flex items-center gap-1 px-3 py-2"
                    title="Clear all received emails"
                  >
                    <Trash2 size={12} /> Clear all received
                  </button>
                </div>
              )}
              {receivedEmails.length === 0 ? (
                <p className="text-muted text-sm py-10 text-center">No received emails yet.</p>
              ) : (
                <div className="space-y-2">
                  {receivedEmails.map((e) => (
                    <div key={e.email_id} className="tile-spec p-4">
                      <button
                        onClick={() => expandEmail(e, e.email_id)}
                        className="w-full flex items-start justify-between gap-3 text-left"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-foreground font-medium truncate">
                            {e.category === "contact_form" ? (
                              <span className="inline-flex items-center gap-1.5"><MessageSquare size={12} /> Contact Form</span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5"><Reply size={12} /> Client Reply</span>
                            )}
                            {" · "}{e.subject}
                          </p>
                          <p className="text-xs text-faint mt-0.5">
                            From: {e.from_name || e.from_email} · {fmtTime(e.created_at)}
                          </p>
                        </div>
                        <div className="shrink-0">
                          {expandedId === e.email_id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </div>
                      </button>

                      <AnimatePresence>
                        {expandedId === e.email_id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 pt-4 border-t border-stroke space-y-2 text-sm text-foreground/80">
                              {loadingThread === e.email_id ? (
                                <div className="flex items-center justify-center py-4"><Loader2 size={14} className="animate-spin text-muted" /></div>
                              ) : (
                                <>
                                  {(threads[e.email_id] || [e]).map((m, idx) => (
                                    <div key={m.email_id} className={`p-3 rounded ${idx > 0 ? 'mt-3' : ''}`} style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs text-faint">
                                          <span className="text-foreground font-medium">{m.direction === 'sent' ? 'To' : 'From'}:</span> {m.from_name || m.from_email}
                                          {m.direction === 'sent' ? ` → ${recipientList(m.to_emails)}` : ''}
                                        </div>
                                        <span className="text-[10px] text-faint shrink-0 ml-2">{fmtTime(m.created_at)}</span>
                                      </div>
                                      <div className="text-sm leading-relaxed inbox-body" dangerouslySetInnerHTML={{ __html: m.body_html || '<em class="text-faint">(No content)</em>' }} />
                                    </div>
                                  ))}
                                  <div className="mt-3 pt-3 border-t border-stroke">
                                    <p className="text-xs text-faint mb-2">Reply to: {e.from_name || e.from_email}</p>
                                    <textarea
                                      className="input-luxe w-full"
                                      rows={2}
                                      value={replyTexts[e.email_id] || ''}
                                      onChange={(ev) => { setReplyTexts(prev => ({ ...prev, [e.email_id]: ev.target.value })); }}
                                      placeholder="Type your reply..."
                                    />
                                    <div className="flex gap-2 mt-2 justify-end">
                                      <button className="btn-primary" style={{ padding: "6px 12px", fontSize: "11px" }} onClick={() => handleReply(e.email_id)} disabled={sendingReply}>
                                        {sendingReply ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                                        Send
                                      </button>
                                      {isOwner && (
                                        <button onClick={() => handleDelete(e.email_id)} className="btn-secondary" style={{ padding: "6px 12px", fontSize: "11px", color: "#F44336" }}>
                                          <Trash2 size={11} /> Delete
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── SEND EMAIL ────────────────────────── */}
          {tab === "send" && (
            <form onSubmit={handleDirectSend} className="tile-spec p-6 max-w-xl space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted mb-2">To *</label>
                <input type="email" className="input-luxe w-full" value={directTo} onChange={(e) => setDirectTo(e.target.value)} required placeholder="client@example.com" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted mb-2">Subject *</label>
                <input className="input-luxe w-full" value={directSubject} onChange={(e) => setDirectSubject(e.target.value)} required placeholder="Subject" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted mb-2">Message *</label>
                <textarea className="input-luxe w-full" rows={6} value={directBody} onChange={(e) => setDirectBody(e.target.value)} required placeholder="Type your message..." />
              </div>
              <div className="flex justify-end">
                <button type="submit" className="btn-primary" disabled={sendingDirect}>
                  {sendingDirect ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {sendingDirect ? "Sending..." : "Send Email"}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
